import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ShoppingBag, MessageCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart, buildWhatsAppUrl } from "../context/CartContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Grain } from "../components/Grain";
import { FloatingWhatsApp } from "../components/FloatingWhatsApp";
import { ChiliHeat } from "../components/ChiliHeat";
import { VegDot } from "../components/VegDot";
import { ProductCard } from "../components/ProductCard";
import { SpiceNote } from "../components/SpiceNote";
import { PincodeCheck } from "../components/PincodeCheck";
import { bySlug, byId, products, byCategory, discountPct, LAUNCH_PROMO } from "../data/products";
import { copy } from "../data/copy";
const ProductPage = () => {
    const { slug } = useParams();
    const product = bySlug(slug) || byId(slug);
    const { lang, t } = useLang();
    const { add, setOpen } = useCart();

    const [size, setSize] = useState(product?.sizes?.[0] || null);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);

    const related = useMemo(() => {
        if (!product) return [];
        return byCategory(product.category)
            .filter((p) => p.id !== product.id)
            .slice(0, 3);
    }, [product]);

    if (!product) {
        return (
            <div className="min-h-screen bg-[color:var(--kp-cream)]">
                <Grain />
                <Header />
                <div className="pt-40 pb-40 max-w-3xl mx-auto px-6 md:px-10 text-center">
                    <h1 className="font-serif-display text-6xl mb-4">Not found</h1>
                    <p className="text-[color:var(--kp-ink)]/70 mb-6">This pickle isn't on our shelf.</p>
                    <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-3 rounded-full text-[12px] tracking-mono-wide"
                    >
                        Browse the shop
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const totalPrice = (size?.price || 0) * qty;
    const totalMrp = (size?.mrp || 0) * qty;
    const off = discountPct(size?.mrp || 0, size?.price || 0);
    const spiceLabel = t(copy.products.spice_labels[product.spice]);

    const waMessage = `Hello Kumari Pickles\n\nI'd like to order:\n• ${product.name.en} (${product.name.te}) — ${size?.weight} × ${qty}\n\nPlease share delivery details & final total.`;

    const addToCart = () => {
        for (let i = 0; i < qty; i++) add(product, size);
        setOpen(true);
    };

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]" data-testid={`product-page-${product.id}`}>
            <Grain />
            <Header />
            <main className="pt-24 md:pt-32 pb-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-red)] mb-6"
                        data-testid="pdp-back-shop"
                    >
                        <ArrowLeft size={12} /> {t({ en: "All pickles", te: "అన్ని ఊరగాయలు" })}
                    </Link>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14">
                        {/* Images */}
                        <div className="md:col-span-7">
                            <div className="clipped-frame aspect-[4/5]">
                                <img
                                    src={product.images?.[activeImg] || product.image}
                                    alt={product.name.en}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid="pdp-main-image"
                                />
                                <div className="absolute inset-0 ring-1 ring-inset ring-[color:var(--kp-ink)]/20 pointer-events-none" />
                                <div className="absolute top-3 left-3 text-[10px] tracking-mono-wide text-[color:var(--kp-cream)] mix-blend-difference">
                                    №{String(products.findIndex((p) => p.id === product.id) + 1).padStart(2, "0")}
                                </div>
                            </div>
                            {product.images && product.images.length > 1 && (
                                <div className="flex gap-2 mt-3">
                                    {product.images.map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveImg(i)}
                                            className={`clipped-frame w-16 h-20 border ${
                                                activeImg === i
                                                    ? "border-[color:var(--kp-red)]"
                                                    : "border-transparent"
                                            }`}
                                            data-testid={`pdp-thumb-${i}`}
                                        >
                                            <img
                                                src={img}
                                                alt=""
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="md:col-span-5">
                            <div className="flex items-center gap-2 mb-3">
                                <VegDot isVeg={product.isVeg} size={12} />
                                <span className="text-[10px] tracking-mono-wide text-[color:var(--kp-ink)]/60 uppercase">
                                    {product.category === "sweet"
                                        ? t({ en: "Sweet Preserve", te: "తీపి" })
                                        : product.isVeg
                                          ? t({ en: "Vegetarian", te: "శాకాహారం" })
                                          : t({ en: "Non-Vegetarian", te: "మాంసాహారం" })}
                                </span>
                                <span className="opacity-30">·</span>
                                <span className="text-[10px] tracking-mono-wide text-[color:var(--kp-red)] uppercase">
                                    {spiceLabel}
                                </span>
                            </div>
                            <motion.h1
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                                className="font-display text-5xl md:text-6xl leading-[0.98] tracking-editorial mb-2 font-bold"
                                data-testid="pdp-title"
                            >
                                {lang === "en"
                                    ? `${product.name.en} (${product.name.te})`
                                    : `${product.name.te} (${product.name.en})`}
                            </motion.h1>
                            <div className="font-body text-xs tracking-mono-wide text-[color:var(--kp-brown)]/70 mb-6" data-testid="pdp-sku">
                                {t({ en: "Product code", te: "ప్రొడక్ట్ కోడ్" })}: <strong>{size?.sku || `KP-${product.code}`}</strong>
                            </div>
                            <p
                                className={`text-lg text-[color:var(--kp-ink)]/80 leading-relaxed mb-6 ${
                                    lang === "te" ? "font-telugu-body" : ""
                                }`}
                            >
                                {t(product.subtitle)}
                            </p>

                            <ChiliHeat level={product.heat} label={t(copy.products.heat)} />

                            {/* Sizes */}
                            <div className="mt-8">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                        {t({ en: "Pack size", te: "ప్యాక్ సైజ్" })}
                                    </div>
                                    {off > 0 && (
                                        <div
                                            className="text-[10px] tracking-mono-wide font-bold text-[color:var(--kp-cream)] bg-[color:var(--kp-terracotta)] px-2.5 py-1 rounded-full"
                                            data-testid="pdp-promo-badge"
                                        >
                                            {t(LAUNCH_PROMO.label)} · {off}% {t({ en: "OFF", te: "తగ్గింపు" })}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s.weight}
                                            onClick={() => setSize(s)}
                                            className={`px-4 py-3 rounded-full text-[12px] tracking-mono-wide border transition-colors ${
                                                size?.weight === s.weight
                                                    ? "border-[color:var(--kp-red)] bg-[color:var(--kp-red)] text-[color:var(--kp-cream)]"
                                                    : "border-[color:var(--kp-ink)]/25 hover:border-[color:var(--kp-ink)]"
                                            }`}
                                            data-testid={`size-${s.weight}`}
                                        >
                                            {s.weight} · ₹{s.price}
                                            {s.mrp > s.price && (
                                                <span className="ml-1.5 line-through opacity-55">₹{s.mrp}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[11px] text-[color:var(--kp-ink)]/55 mt-2" data-testid="pdp-promo-note">
                                    {t(LAUNCH_PROMO.note)}
                                </p>
                                <p className="text-[11px] leading-snug text-[color:var(--kp-terracotta)] mt-1.5 font-bold" data-testid="pdp-250g-note">
                                    {t({
                                        en: "250 g packs are sold only to Andhra Pradesh and Telangana pincodes.",
                                        te: "250 గ్రా. ప్యాకెట్లు ఆంధ్రప్రదేశ్, తెలంగాణ పిన్‌కోడ్‌లకు మాత్రమే.",
                                    })}
                                </p>
                            </div>

                            {/* Quantity */}
                            <div className="mt-6 flex items-center gap-4">
                                <div className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                    {t({ en: "Quantity", te: "పరిమాణం" })}
                                </div>
                                <div className="flex items-center gap-2 border border-[color:var(--kp-ink)]/25 rounded-full px-2 py-1">
                                    <button
                                        onClick={() => setQty(Math.max(1, qty - 1))}
                                        className="p-1"
                                        data-testid="pdp-qty-decrement"
                                    >
                                        <Minus size={13} />
                                    </button>
                                    <span className="w-6 text-center" data-testid="pdp-qty">{qty}</span>
                                    <button
                                        onClick={() => setQty(qty + 1)}
                                        className="p-1"
                                        data-testid="pdp-qty-increment"
                                    >
                                        <Plus size={13} />
                                    </button>
                                </div>
                            </div>

                            {/* Total + CTAs */}
                            <div className="mt-8 pt-6 border-t border-[color:var(--kp-ink)]/12">
                                <div className="flex items-baseline justify-between mb-2">
                                    <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                        {t({ en: "Total", te: "మొత్తం" })}
                                    </span>
                                    <span className="flex items-baseline gap-2">
                                        {totalMrp > totalPrice && (
                                            <span className="text-base text-[color:var(--kp-ink)]/45 line-through" data-testid="pdp-total-mrp">
                                                ₹{totalMrp}
                                            </span>
                                        )}
                                        <span className="font-serif-display text-3xl" data-testid="pdp-total">
                                            ₹{totalPrice}
                                        </span>
                                    </span>
                                </div>
                                {totalMrp > totalPrice && (
                                    <div className="text-[11px] text-[color:var(--kp-banana)] font-bold mb-4" data-testid="pdp-savings">
                                        {t({ en: `You save ₹${totalMrp - totalPrice}`, te: `మీరు ₹${totalMrp - totalPrice} ఆదా చేస్తున్నారు` })}
                                    </div>
                                )}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={addToCart}
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[12px] tracking-mono-wide hover:bg-black"
                                        data-testid="pdp-add-to-cart"
                                    >
                                        <ShoppingBag size={14} />
                                        {t({ en: "Add to basket", te: "బాస్కెట్‌కు చేర్చు" })}
                                    </button>
                                    <a
                                        href={buildWhatsAppUrl(waMessage)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[12px] tracking-mono-wide hover:bg-[color:var(--kp-red-deep)]"
                                        data-testid="pdp-whatsapp"
                                    >
                                        {t(copy.products.whatsapp)}
                                    </a>
                                </div>
                            </div>

                            {/* Spice-level customisation note (shown on every ordering surface) */}
                            <SpiceNote className="mt-4" testId="pdp-spice-custom-note" />

                            <div
                                className="mt-3 flex items-start gap-2 rounded-2xl bg-[color:var(--kp-banana)]/10 border border-[color:var(--kp-banana)]/35 px-4 py-3"
                                data-testid="pdp-reachout-note"
                            >
                                <MessageCircle size={13} className="mt-0.5 shrink-0 text-[color:var(--kp-banana)]" />
                                <p className="text-[12px] leading-snug text-[color:var(--kp-ink)]/80">
                                    {t({
                                        en: "We will reach out to you on WhatsApp or call to know if you have any specifications before we start the preparations.",
                                        te: "తయారీ ప్రారంభించే ముందు మీకు ఏవైనా ప్రత్యేక సూచనలు ఉన్నాయా అని తెలుసుకోవడానికి మేము WhatsApp లో లేదా ఫోన్‌లో మిమ్మల్ని సంప్రదిస్తాము.",
                                    })}
                                </p>
                            </div>

                            <PincodeCheck />

                            {/* Details */}
                            <dl className="mt-10 space-y-6 text-sm">
                                <Detail
                                    label={t({ en: "Ingredients", te: "పదార్థాలు" })}
                                    body={t(product.ingredients)}
                                    telugu={lang === "te"}
                                    testId="pdp-ingredients"
                                />
                                {product.allergens && (
                                    <Detail
                                        label={t({ en: "Allergens", te: "అలర్జీ" })}
                                        body={t(product.allergens)}
                                        telugu={lang === "te"}
                                        testId="pdp-allergens"
                                    />
                                )}
                                <Detail
                                    label={t({ en: "Shelf life", te: "వినియోగ కాలం" })}
                                    body={t(product.shelfLife)}
                                    telugu={lang === "te"}
                                    testId="pdp-shelf"
                                />
                                <Detail
                                    label={t({ en: "Storage instructions", te: "నిల్వ సూచనలు" })}
                                    telugu={lang === "te"}
                                    testId="pdp-storage"
                                    body={
                                        <ul className="space-y-1.5 list-disc pl-4">
                                            {product.storage.map((s, i) => (
                                                <li key={i}>{t(s)}</li>
                                            ))}
                                        </ul>
                                    }
                                />
                                <Detail
                                    label={t({ en: "Packing", te: "ప్యాకింగ్" })}
                                    body={t(product.packaging)}
                                    telugu={lang === "te"}
                                    testId="pdp-packaging"
                                />
                            </dl>
                        </div>
                    </div>

                    {/* Related */}
                    {related.length > 0 && (
                        <div className="mt-24 md:mt-32 border-t border-[color:var(--kp-ink)]/12 pt-16">
                            <div className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 mb-8">
                                {t({ en: "From the same shelf", te: "ఇదే అరనుండి" })}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
                                {related.map((p, i) => (
                                    <ProductCard key={p.id} product={p} index={i} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

const Detail = ({ label, body, telugu, testId }) => (
    <div className="grid grid-cols-12 gap-4 pb-4 border-b border-[color:var(--kp-ink)]/10" data-testid={testId}>
        <dt className="col-span-4 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
            {label}
        </dt>
        <dd className={`col-span-8 text-[color:var(--kp-ink)]/85 ${telugu ? "font-telugu-body" : ""}`}>
            {body}
        </dd>
    </div>
);

export default ProductPage;
